import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { QuillEditorComponent } from '../../quill/quill-editor.component';
import { CollaborationService } from '../../services/collaboration.service';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
@Component({
    selector: 'exp-quill-rte',
    templateUrl: './quill-rte.component.html',
    styleUrls: ['./quill-rte.component.scss'],
    imports: [QuillEditorComponent, FormsModule, ReactiveFormsModule]
})
export class QuillRTEComponent implements OnInit, OnDestroy {
  @Output() updateValueChange = new EventEmitter();
  @Input() placeHolder: string = "What's on your mind?";
  @Input() data: any = '';
  @Input() postDetails: any = 'html-draft-post';
  @Input() isEditFlow: boolean = false;
  @Input() openEditForm: Boolean = false;
  @Input() atMentionConfig: any;
  editor: any;
  showError = false;
  control: FormControl = new FormControl('', [Validators.required]);
  defaultValue = '<p style="font-size: 14px;">hello </p>';
  initialValue = '';
  id = uuidv4();
  tempRTEData = '';
  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    public dialog: MatDialog,
    private collaborationService: CollaborationService
  ) { }

  ngOnInit() {
    if (this.collaborationService?.allPostSpaceDetails?.spaceId) {
      this.postDetails = this.postDetails === 'html-draft-post' ? this.collaborationService.allPostSpaceDetails.spaceId + '-' + this.postDetails : this.collaborationService.allPostSpaceDetails.spaceId + '-' + this.postDetails?.postId + '-html-draft-comment';
      if (this.data === '') {
        this.data = localStorage.getItem(this.postDetails);
      }
    }
    this.initialValue = this.data === '' ? null : this.data;
    this.control.patchValue(this.data);
    this.collaborationService.onMessageSuccessEvent
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res.id === this.id) {
          if (res.success) {
            this.collaborationService.editorDataModified[this.id] = false;
            this.control.setValue('');
            this.showError = false;
          } else {
            this.control.setValue(this.tempRTEData);
            this.editor.focus();
            this.editor.setSelection(this.editor.getLength(), 0, 'api');
          }
        }
      });
    this.collaborationService.clearRTEData
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.control.setValue('');
      });
  }

  send() {
    this.showError = false;

    // const tableRegex = /<div class="([^"]+)"><table class="([^"]+)"><colgroup><col width="([^"]+)"><\/colgroup><\/table><\/div>/;
    // const emptyRegex = /<p(?:\s+\w+="[^"]*")*\s*>\s*<\/p>/;
    if (this.control.invalid || (this.editor.root.innerText.trim() == '' && !this.control.value.includes('<img') && !this.control.value.includes('<table'))) {
      this.control.reset()
      this.showError = true;
      return;
    }
    const textData = document.createElement('div');
    textData.innerHTML = this.collaborationService.decodeHtmlTagsOnly(this.control.value);
    let text = textData.innerHTML;

    //Length Calculation
    let maxCharacterLimit = 40000;
    if (text.includes('<img')) {
      let lengthWithoutImage = this.calculateContentLengthWithoutImage(textData.innerHTML);
      if (lengthWithoutImage <= 40000 && text.length <= 120000) {
        maxCharacterLimit = 120000;
      }
    }

    // if table has a col width more than 100, then replace col width with auto for table to not get cut out in pdf
    if (text.includes('quill-better-table-wrapper') && /col width="([1-9][0-9]{2,})"/.test(text)) {
      const updatedText = text.replace(
        /col width="([1-9][0-9]{2,}|[1-9][1-9]?)"/g,
        'col width="auto"'
      );
      text = updatedText;
      if (/style="width:\s*\d+px;"/.test(text)) {
        const finalText = text.replace(/style="width:\s*\d+px;"/g, '');
        text = finalText;
      }
    }

    textData.remove();

    if (text.length > maxCharacterLimit) {
      let msg =
        'The post exceeds the maximum limit of 40,000 characters. Please be aware that rich text formatting—such as tables, colors, and bold or underlined text—contributes to the overall character count. For substantial content, we suggest attaching a document in the referral documents or policy documents section and referencing the file here using #.';
      if (text.includes('<img')) {
        msg =
          'The text and image within the post exceeds the 40,000 character limit. Please be aware that both rich text formatting - such as tables, colors, bold, or underlined text - as well as the image size, contributes to the overall character count. For substantial content, we suggest attaching a document in the referral documents or policy documents section and referencing the file here using a #.';
      }
      this.dialog.open(ConfirmationDialogComponent, {
        data: {
          title: 'Maximum Length Exceeded',
          message: msg,
          buttonText: 'ok',
        },
        autoFocus: false
      });
      return;
    }
    this.tempRTEData = this.control.value;
    this.control.reset();
    this.removeLocalStorageDraft();
    this.updateValueChange.emit({ text, id: this.id });
  }

  calculateContentLengthWithoutImage(content: string) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');

    // Remove all table elements
    const tables = doc.querySelectorAll('img');
    tables.forEach(table => {
      table.remove();
    });

    // Get the text content of the document after removing the tables
    const textContent = doc.body.textContent || "";
    return textContent.trim().length;
  }

  removeLocalStorageDraft() {
    if (localStorage.getItem(this.postDetails)) {
      localStorage.removeItem(this.postDetails);
    }
  }

  onEditorContentChanged(event: any) {
    if ((event.html || localStorage.getItem(this.postDetails)) && !this.isEditFlow) {
      localStorage.setItem(this.postDetails, event.html ? event.html : '');
    }
    this.collaborationService.editorDataModified[this.id] =
      event.html === this.initialValue ? false : true;
  }
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
  setFocus(editor: any) {
    this.editor = editor;
    if (this.isEditFlow) {
      editor.focus();
      editor.setSelection(editor.getLength(), 0, 'api');
    }
  }
}
